import React, { forwardRef, ForwardedRef } from 'react'
import { Textarea } from '@machobear/design-system'
import { useField } from 'formik'
import noop from 'lodash/noop'
import { useTranslation } from 'react-i18next'

type TextareaProps = React.ComponentProps<typeof Textarea>

export const TextArea = forwardRef(
  (
    {
      name,
      label,
      onChange = noop,
      onFocus = noop,
      onBlur = noop,
      onKeyDown = noop,
      required = false,
      grow = true,
      lineLimit,
      lines,
      characterLimit,
      disabled = false,
      actions,
      highlightRanges,
      resize
    }: {
      name: string
      lineLimit?: number
      label: string
      onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
      onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
      onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
      grow?: boolean
    } & Pick<
      TextareaProps,
      | 'actions'
      | 'onBlur'
      | 'highlightRanges'
      | 'lines'
      | 'characterLimit'
      | 'disabled'
      | 'required'
      | 'resize'
    >,
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const [field, meta] = useField(name)
    const { error, touched } = meta
    const { value, onChange: formikOnChange, onBlur: formikOnBlur } = field
    const { t } = useTranslation()

    const onChangeField = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e)
      formikOnChange(e)
    }

    const onBlurField = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      onBlur(e)
      formikOnBlur(e)
    }

    const getErrorMessage = () => {
      if (characterLimit && value.length > characterLimit) {
        return t(
          'l.settings.view.auto_reply_character_limit.error',
          'Message must be at most 4000 characters'
        )
      } else {
        return error
      }
    }

    return (
      <Textarea
        ref={ref}
        id={name}
        name={name}
        lineLimit={lineLimit}
        value={value}
        onChange={onChangeField}
        onBlur={onBlurField}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        label={label}
        lines={lines}
        highlightRanges={highlightRanges}
        actions={actions}
        characterLimit={characterLimit}
        required={required}
        disabled={disabled}
        grow={grow}
        resize={resize}
        message={error && touched ? getErrorMessage() : null}
        tone={error && touched ? 'critical' : 'neutral'}
      />
    )
  }
)
